import { useMemo } from "react";
import { ActivityList } from "./ActivityList";
import { I_Building, I_Cal, I_Chev, I_Flame, I_Pulse, I_Snooze } from "./icons";
import { Ava } from "./ui";
import { fmtINR, timeAgo } from "../lib/format.js";
import { glass, glassHov, heading, monoFont } from "../styles.js";

const Dashboard = ({
    clients,
    properties,
    visits,
    activity,
    onOpenClient,
    onOpenProperty,
    onNav,
  }) => {
    const m = useMemo(() => {
      const activeClients = clients.filter((c) => c.status === "Active").length;
      const availProps = properties.filter((p) => p.status === "Available").length;
      const allotted = clients.filter((c) => !!c.allotted).length;
      const conversion = clients.length ? Math.round((allotted / clients.length) * 100) : 0;
      // hot properties: most visits in last 30 days
      const thirty = Date.now() - 30 * 86400 * 1000;
      const visitCount = {};
      visits.forEach((v) => {
        if (v.property_id && new Date(v.visited_at).getTime() > thirty)
          visitCount[v.property_id] = (visitCount[v.property_id] || 0) + 1;
      });
      const hot = properties
        .map((p) => ({
          ...p,
          vc: visitCount[p.id] || 0,
        }))
        .filter((p) => p.vc > 0)
        .sort((a, b) => b.vc - a.vc)
        .slice(0, 5);
      // stale clients: active + no followup nor visit in 30+ days
      const lastTouch = {};
      visits.forEach((v) => {
        if (v.client_id)
          lastTouch[v.client_id] = Math.max(
            lastTouch[v.client_id] || 0,
            new Date(v.visited_at).getTime(),
          );
      });
      // (followups are loaded inside client detail; use updated_at as proxy here)
      const stale = clients
        .filter((c) => c.status === "Active")
        .map((c) => ({
          ...c,
          lt: Math.max(lastTouch[c.id] || 0, new Date(c.updated_at).getTime()),
        }))
        .filter((c) => Date.now() - c.lt > 30 * 86400 * 1000)
        .sort((a, b) => a.lt - b.lt)
        .slice(0, 5);
      // upcoming visits
      const upcoming = visits
        .filter((v) => v.is_scheduled && new Date(v.visited_at).getTime() > Date.now())
        .sort((a, b) => new Date(a.visited_at) - new Date(b.visited_at))
        .slice(0, 5);
      return {
        activeClients,
        availProps,
        allotted,
        conversion,
        hot,
        stale,
        upcoming,
      };
    }, [clients, properties, visits]);
    const Metric = ({ label: lbl, value, sub, accent, onClick }) => (
      <button
        onClick={onClick}
        className={`${glass} ${glassHov} rounded-2xl p-5 text-left transition`}
      >
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium mb-2">
          {lbl}
        </div>
        <div className="text-4xl text-slate-100 leading-none mb-1" style={heading}>
          {value}
        </div>
        <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
          {sub} <span className={`w-1.5 h-1.5 rounded-full ${accent || "bg-amber-300"}`} />
        </div>
      </button>
    );
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl text-slate-100 mb-1" style={heading}>
            Overview
          </h1>
          <p className="text-sm text-slate-400">A clear read on your pipeline.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Metric
            label="Active clients"
            value={m.activeClients}
            sub={`${clients.length} total`}
            accent="bg-amber-300"
            onClick={() => onNav("clients")}
          />
          <Metric
            label="Available"
            value={m.availProps}
            sub={`${properties.length} listed`}
            accent="bg-teal-300"
            onClick={() => onNav("properties")}
          />
          <Metric
            label="Allotted"
            value={m.allotted}
            sub={`${m.conversion}% conversion`}
            accent="bg-emerald-300"
            onClick={() => onNav("clients")}
          />
          <Metric
            label="Upcoming visits"
            value={m.upcoming.length}
            sub="next 7 days"
            accent="bg-sky-300"
            onClick={() => onNav("visits")}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className={`${glass} rounded-2xl p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-slate-200">
                <I_Flame size={16} className="text-amber-300" />
                <h2 className="text-lg" style={heading}>
                  Hot properties
                </h2>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                30d visits
              </span>
            </div>
            {m.hot.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No visits logged yet.</div>
            ) : (
              <div className="space-y-2">
                {m.hot.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onOpenProperty(p.id)}
                    className="w-full text-left p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.04] hover:border-white/[0.10] transition flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-white/[0.04] overflow-hidden flex items-center justify-center shrink-0">
                      {p.cover_photo ? (
                        <img src={p.cover_photo} className="w-full h-full object-cover" />
                      ) : (
                        <I_Building className="text-slate-500" size={18} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">{p.title}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {p.locality || p.type || "—"} · {fmtINR(p.price_lakhs)}
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-1 px-2 py-1 rounded bg-amber-300/10 text-amber-200 text-xs font-medium"
                      style={monoFont}
                    >
                      {p.vc}
                      <span className="text-[9px] opacity-70">×</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={`${glass} rounded-2xl p-5`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-slate-200">
                <I_Snooze size={16} className="text-violet-300" />
                <h2 className="text-lg" style={heading}>
                  Stale leads
                </h2>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                no touch 30d+
              </span>
            </div>
            {m.stale.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                All your active clients have recent activity. 👏
              </div>
            ) : (
              <div className="space-y-2">
                {m.stale.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => onOpenClient(c.id)}
                    className="w-full text-left p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.04] hover:border-white/[0.10] transition flex items-center gap-3"
                  >
                    <Ava name={c.name} photo={c.photo} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">{c.name}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {c.requirement_type || "—"} · {c.budget_bucket || "—"}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 shrink-0">
                      {timeAgo(new Date(c.lt).toISOString())}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={`${glass} rounded-2xl p-5`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-slate-200">
              <I_Cal size={16} className="text-sky-300" />
              <h2 className="text-lg" style={heading}>
                Upcoming visits
              </h2>
            </div>
            <button
              onClick={() => onNav("visits")}
              className="text-[11px] text-slate-400 hover:text-slate-200 flex items-center gap-0.5"
            >
              All visits
              <I_Chev size={12} />
            </button>
          </div>
          {m.upcoming.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No upcoming site visits.</div>
          ) : (
            <div className="space-y-2">
              {m.upcoming.map((v) => {
                const c = clients.find((x) => x.id === v.client_id),
                  p = properties.find((x) => x.id === v.property_id);
                return (
                  <div
                    key={v.id}
                    className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] flex items-center gap-3"
                  >
                    <div className="w-12 text-center shrink-0">
                      <div className="text-[10px] uppercase tracking-wider text-sky-300">
                        {new Date(v.visited_at).toLocaleDateString("en-IN", {
                          month: "short",
                        })}
                      </div>
                      <div className="text-xl text-slate-100" style={heading}>
                        {new Date(v.visited_at).getDate()}
                      </div>
                      <div className="text-[10px] text-slate-500" style={monoFont}>
                        {new Date(v.visited_at).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 truncate">
                        {c?.name || v.walkin_name || "Walk-in"}{" "}
                        <span className="text-slate-500">·</span>{" "}
                        <span className="text-slate-300">{p?.title || "—"}</span>
                      </div>
                      {v.note && (
                        <div className="text-[11px] text-slate-400 truncate">{v.note}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className={`${glass} rounded-2xl p-5`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-slate-200">
              <I_Pulse size={16} className="text-emerald-300" />
              <h2 className="text-lg" style={heading}>
                Recent activity
              </h2>
            </div>
            <button
              onClick={() => onNav("activity")}
              className="text-[11px] text-slate-400 hover:text-slate-200 flex items-center gap-0.5"
            >
              Full feed
              <I_Chev size={12} />
            </button>
          </div>
          {activity.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No activity yet.</div>
          ) : (
            <ActivityList
              items={activity.slice(0, 8)}
              clients={clients}
              properties={properties}
              onOpenClient={onOpenClient}
              onOpenProperty={onOpenProperty}
              compact={true}
            />
          )}
        </div>
      </div>
    );
  };

export { Dashboard };
